<template>
  <div
    :class="['dispatch-modal-overlay', { show: show }]"
    v-show="show"
    @click.self="close"
  >
    <div class="dispatch-modal-container" :class="{ 'modal-enter-active': show }">
      <div class="dispatch-modal-content">
        <!-- Header -->
        <div class="dispatch-header">
          <div class="header-content">
            <div class="title-section">
              <div class="dispatch-icon">
                <i class="bi bi-send-check-fill"></i>
              </div>
              <div class="title-content">
                <h2 class="dispatch-title">{{ title }}</h2>
                <div class="dispatch-subtitle">
                  Select agencies to dispatch for emergency #{{ emergencyId }}
                  <span v-if="dispatchedCount > 0" class="dispatched-badge">
                    {{ dispatchedCount }} already dispatched
                  </span>
                </div>
              </div>
            </div>
            <button @click="close" type="button" class="close-btn" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Agency Selection Grid -->
        <div class="dispatch-body">
          <div class="agency-grid">
            <SpinnerBlock v-if="isProcessing" />

            <div
              v-else
              v-for="agency in agencies"
              :key="agency.agencyId"
              :class="[
                'agency-card',
                {
                  selected: selectedAgencies.includes(agency.agencyId),
                  dispatched: isAgencyDispatched(agency.agencyId),
                },
              ]"
              @click="toggleAgency(agency.agencyId)"
            >
              <div class="agency-icon" :class="getAgencyType(agency.agencyName)">
                <i :class="getAgencyIcon(agency.agencyName)"></i>
                <div
                  v-if="isAgencyDispatched(agency.agencyId)"
                  class="dispatched-indicator"
                >
                  <i class="bi bi-check-circle-fill"></i>
                </div>
              </div>
              <div class="agency-info">
                <h3 class="agency-name">{{ agency.agencyName }}</h3>
                <p class="agency-description">{{ agency.agencyDescription }}</p>

                <!-- Responders List -->
                <div
                  class="responders-list"
                  v-if="agency.responders && agency.responders.length > 0"
                >
                  <div class="responders-label">Available Responders:</div>
                  <div class="responder-items">
                    <div
                      v-for="responder in agency.responders"
                      :key="responder.id"
                      class="responder-item"
                    >
                      <i class="bi bi-person-check"></i>
                      <span class="responder-name">{{ responder.fullName }}</span>
                      <!-- <span
                        class="responder-badge"
                        :class="{ verified: responder.isIdentityVerified }"
                      >
                        {{ responder.isIdentityVerified ? "Verified" : "Pending" }}
                      </span> -->
                      <span
                        class="responder-badge"
                        :class="{ verified: responder.dispatchStatus === 'Available' }"
                      >
                        {{ responder.dispatchStatus }}
                      </span>
                      <br />
                      <div
                        class="d-flex justify-content-end"
                        v-if="responder?.dispatchStatus !== 'Available'"
                      >
                        <button
                          @click.stop.prevent="forceRelease(responder.id)"
                          class="btn btn-danger"
                        >
                          Force Release
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-responders">
                  <i class="bi bi-exclamation-triangle"></i>
                  No responders available
                </div>

                <div class="agency-status">
                  <span class="status-badge" :class="getStatusClass(agency.agencyId)">
                    {{ getStatusText(agency.agencyId) }}
                  </span>
                  <span class="responder-count">
                    {{ agency.responders?.length || 0 }} responder{{
                      agency.responders?.length !== 1 ? "s" : ""
                    }}
                  </span>
                </div>
              </div>
              <div class="selection-indicator">
                <i class="bi bi-check-lg"></i>
              </div>
            </div>
          </div>

          <!-- Quick Select Buttons -->
          <div
            class="quick-select"
            :class="{ disabled: isProcessing }"
            v-if="showQuickSelect"
          >
            <div class="quick-select-label">Quick Select:</div>
            <div class="quick-buttons">
              <button
                v-for="preset in quickPresets"
                :key="preset.id"
                class="quick-btn"
                :class="preset.type"
                @click="applyPreset(preset.agencies)"
              >
                <i :class="preset.icon"></i>
                {{ preset.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- Footer with Actions -->
        <div class="dispatch-footer" :class="{ disabled: isProcessing }">
          <div class="footer-content">
            <!-- Selected Summary -->
            <div class="selected-summary">
              <div v-if="selectedAgencies.length > 0" class="summary-content">
                <i class="bi bi-check-circle"></i>
                <span class="summary-text">
                  <strong>{{ selectedAgencies.length }}</strong>
                  {{ selectedAgencies.length === 1 ? "agency" : "agencies" }} selected
                </span>
                <div class="selected-tags">
                  <span
                    v-for="agencyId in selectedAgencies"
                    :key="agencyId"
                    class="selected-tag"
                  >
                    {{ getAgencyName(agencyId) }}
                  </span>
                </div>
              </div>
              <div v-else class="no-selection">
                <i class="bi bi-exclamation-circle"></i>
                Select at least one agency to dispatch
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
              <button class="btn btn-secondary" @click="close">
                <i class="bi bi-x-circle"></i>
                Cancel
              </button>
              <button
                class="btn btn-primary"
                :class="{ disabled: selectedAgencies.length === 0 }"
                @click="dispatchTeams"
                :disabled="selectedAgencies.length === 0"
              >
                <i class="bi bi-send-fill"></i>
                Dispatch Selected Teams
                <span v-if="selectedAgencies.length > 0" class="selected-count">
                  ({{ selectedAgencies.length }})
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import agencyService from "@/services/agencyService";
import confirmationService from "@/services/confirmationService";
import emergencyService from "@/services/emergencyService";
import toastService from "@/services/toastService";
import userService from "@/services/userService";
import { ref, computed, watch, onMounted } from "vue";
import SpinnerBlock from "../animations/SpinnerBlock.vue";

// Props
// eslint-disable-next-line
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Dispatch Response Teams",
  },
  emergencyId: {
    type: [String, Number],
    default: "0001",
  },
  dispatchedTeams: {
    type: Object,
    default: () => ({}),
  },
  showQuickSelect: {
    type: Boolean,
    default: true,
  },
});

const isProcessing = ref(false);

const forceRelease = async (id) => {
  const confirmed = await confirmationService.show(
    "This will forcely set the responder status to available.",
    "Are you sure?"
  );

  if (confirmed) {
    isProcessing.value = true;
    const resp = await userService.forceReleaseResponder(id);
    if (resp.success) {
      // Update the responder status in the agencies array
      updateResponderStatus(id, "Available");

      // Optional: Show success message
      //alertService.show("Responder released successfully!", "success");
    } else {
      emit("error", resp.message);
    }
    isProcessing.value = false;
  }
};

// Helper function to update responder status
const updateResponderStatus = (responderId, newStatus) => {
  // Find and update the responder in the agencies array
  agencies.value = agencies.value.map((agency) => {
    // Check if this agency has the responder
    const hasResponder = agency.responders?.some(
      (responder) => responder.id === responderId
    );

    if (hasResponder) {
      // Update the specific responder's status
      return {
        ...agency,
        responders: agency.responders.map((responder) =>
          responder.id === responderId
            ? { ...responder, dispatchStatus: newStatus }
            : responder
        ),
      };
    }

    return agency;
  });
};
// Emits
// eslint-disable-next-line
const emit = defineEmits(["close", "dispatch", "error"]);
const agencies = ref([]);
// Reactive data
const selectedAgencies = ref([]);

// Quick selection presets based on your agencies
const quickPresets = ref([
  {
    id: "emergency_services",
    name: "Emergency Services",
    type: "emergency",
    icon: "bi-shield-check",
    agencies: [], // Will be populated based on available agencies
  },
  {
    id: "all_agencies",
    name: "All Agencies",
    type: "all",
    icon: "bi-building",
    agencies: [], // Will be populated based on available agencies
  },
]);

// Computed properties
const dispatchedCount = computed(() => {
  return Object.keys(props.dispatchedTeams).length;
});

// Initialize quick presets based on available agencies
watch(
  () => agencies.value,
  (newAgencies) => {
    if (newAgencies && newAgencies.length > 0) {
      const allAgencyIds = newAgencies.map((agency) => agency.agencyId);
      const emergencyAgencyIds = newAgencies
        .filter(
          (agency) =>
            agency.agencyName === "POLICE" ||
            agency.agencyName === "FIRE" ||
            agency.agencyName === "COASTGUARD"
        )
        .map((agency) => agency.agencyId);

      quickPresets.value[0].agencies = emergencyAgencyIds;
      quickPresets.value[1].agencies = allAgencyIds;
    }
  },
  { immediate: true }
);
const getActiveAgencies = async () => {
  agencies.value = await agencyService.getActiveAgencies();
};
onMounted(() => {
  getActiveAgencies();
});

// Methods
const close = () => {
  selectedAgencies.value = [];
  emit("close");
};

const toggleAgency = (agencyId) => {
  const index = selectedAgencies.value.indexOf(agencyId);
  const agency = agencies.value.find((a) => a.agencyId === agencyId);

  // Check if agency has any available responders
  const hasAvailableResponders = agency?.responders?.some(
    (x) => x.dispatchStatus === "Available"
  );

  if (!hasAvailableResponders) {
    // Show alert and prevent selection
    emit(
      "error",
      `No available responders in ${agency.agencyName}. All responders are currently busy.`
    );
    return;
  }

  if (index > -1) {
    selectedAgencies.value.splice(index, 1);
  } else {
    selectedAgencies.value.push(agencyId);
  }
};

const isAgencyDispatched = (agencyId) => {
  return props.dispatchedTeams[agencyId] !== undefined;
};

const getStatusClass = (agencyId) => {
  if (isAgencyDispatched(agencyId)) {
    return props.dispatchedTeams[agencyId].status === "enroute"
      ? "enroute"
      : "dispatched";
  }

  // Check responder availability for status class
  const agency = agencies.value.find((a) => a.agencyId === agencyId);
  if (agency && agency.responders && agency.responders.length > 0) {
    const hasAvailableResponder = agency.responders.some(
      (responder) => responder.dispatchStatus === "Available"
    );

    return hasAvailableResponder ? "available" : "busy";
  }

  return "unavailable";
};
const getStatusText = (agencyId) => {
  // First check if agency is already dispatched
  if (isAgencyDispatched(agencyId)) {
    return props.dispatchedTeams[agencyId].status === "enroute"
      ? "En Route"
      : "Dispatched";
  }

  // Check if agency has any available responders
  const agency = agencies.value.find((a) => a.agencyId === agencyId);
  if (agency && agency.responders && agency.responders.length > 0) {
    const hasAvailableResponder = agency.responders.some(
      (responder) => responder.dispatchStatus === "Available"
    );

    return hasAvailableResponder ? "Available" : "Busy";
  }

  // No responders or agency not found
  return "Unavailable";
};
const getAgencyName = (agencyId) => {
  const agency = agencies.value.find((a) => a.agencyId === agencyId);
  return agency ? agency.agencyName : agencyId;
};

const getAgencyType = (agencyName) => {
  const typeMap = {
    POLICE: "police",
    FIRE: "fire",
    COASTGUARD: "rescue",
    PERT: "medical",
    LEYECOV: "utility",
  };
  return typeMap[agencyName] || "default";
};

const getAgencyIcon = (agencyName) => {
  const iconMap = {
    POLICE: "bi-shield-check",
    FIRE: "bi-fire",
    COASTGUARD: "bi-life-preserver",
    PERT: "bi-heart-pulse",
    LEYECOV: "bi-lightning-charge",
  };
  return iconMap[agencyName] || "bi-building";
};

const applyPreset = (agencies) => {
  selectedAgencies.value = [...agencies];
};

const dispatchTeams = async () => {
  if (selectedAgencies.value.length > 0) {
    // Get full agency details for dispatch
    const selectedAgencyDetails = agencies.value
      .filter((agency) => selectedAgencies.value.includes(agency.agencyId))
      .map((agency) => ({
        agencyId: agency.agencyId,
        agencyName: agency.agencyName,
        responders: agency.responders?.map((x) => x.id) || [],
      }));
    const payload = {
      erId: props.emergencyId,
      agencies: selectedAgencyDetails, // This is already the array you need
    };
    const resp = await emergencyService.dispatchTeams(payload);
    if (resp.success) {
      emit("dispatch", selectedAgencyDetails);
      selectedAgencies.value = [];
    } else {
      toastService.error(resp.message);
    }
  }
};

// Watch for modal show to reset selection
watch(
  () => props.show,
  (newVal) => {
    if (!newVal) {
      selectedAgencies.value = [];
    }
  }
);
// eslint-disable-next-line
defineExpose({
  getActiveAgencies,
});
</script>
<style scoped>
/* Dispatch Modal Overlay */

/* MODIFIED: Responders section with overflow protection */
.responders-list {
  margin: 0.75rem 0;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  border-left: 3px solid #a30e0e;
  max-height: 200px;
  overflow-y: auto;
}

.responders-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0;
  backdrop-filter: blur(5px);
}

.responder-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.responder-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  min-width: 0;
}

.responder-item:hover {
  border-color: #a30e0e;
  background: #fff5f5;
}

.responder-item.unavailable {
  background: #fff3e0;
  border-color: #ffc107;
}

.responder-main-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex-wrap: wrap;
}

.responder-item i {
  color: #28a745;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.responder-name {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 500;
  color: #2d3748;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.responder-badge {
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  flex-shrink: 0;
}

.responder-badge.verified {
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.responder-badge.busy {
  background: #ffeaa7;
  color: #e17055;
  border: 1px solid #fdcb6e;
}

.responder-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-force-release {
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
}

.btn-force-release:hover {
  background: linear-gradient(135deg, #c0392b, #a93226);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
}

.btn-force-release:active {
  transform: translateY(0);
}

.no-responders {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #fff3e0;
  border-radius: 6px;
  color: #f57c00;
  font-size: 0.85rem;
  font-style: italic;
  margin: 0.75rem 0;
}

.no-responders i {
  font-size: 1rem;
}

.responder-count {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  background: #e9ecef;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
}

/* Custom scrollbar for responders list */
.responders-list::-webkit-scrollbar {
  width: 4px;
}

.responders-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.responders-list::-webkit-scrollbar-thumb {
  background: #a30e0e;
  border-radius: 2px;
}

.responders-list::-webkit-scrollbar-thumb:hover {
  background: #8a0b0b;
}

/* Agency type colors */
.agency-icon.police {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.agency-icon.fire {
  background: linear-gradient(135deg, #fd7e14, #e55a00);
}

.agency-icon.rescue {
  background: linear-gradient(135deg, #20c997, #17a2b8);
}

.agency-icon.medical {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.agency-icon.utility {
  background: linear-gradient(135deg, #6f42c1, #5a2d9c);
}

.agency-icon.default {
  background: linear-gradient(135deg, #6c757d, #495057);
}

/* Quick button types */
.quick-btn.emergency {
  border-color: #dc3545;
  color: #dc3545;
}

.quick-btn.all {
  border-color: #6f42c1;
  color: #6f42c1;
}

.dispatch-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1090;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dispatch-modal-overlay.show {
  opacity: 1;
  visibility: visible;
}

/* Dispatch Modal Container - FIXED */
.dispatch-modal-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.9);
  width: 90%;
  max-width: 800px;
  max-height: 90vh; /* Changed from 85vh to 90vh for more space */
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1),
    0 0 80px rgba(163, 14, 14, 0.15);
  z-index: 1070;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  display: flex; /* Added */
  flex-direction: column; /* Added */
}

.dispatch-modal-container.modal-enter-active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.dispatch-modal-content {
  display: flex;
  flex-direction: column;
  height: 100%; /* Ensure it takes full height */
  min-height: 0; /* Important for flexbox children */
}

/* Header - Fixed height */
.dispatch-header {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0; /* Prevent header from shrinking */
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.dispatch-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

.dispatch-icon i {
  font-size: 1.5rem;
  color: white;
}

.title-content {
  color: white;
  flex: 1;
}

.dispatch-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.dispatch-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.dispatched-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1) rotate(90deg);
}

/* Body - Now scrollable with flex grow */
.dispatch-body {
  flex: 1; /* This makes it grow and take available space */
  padding: 2rem;
  overflow-y: auto; /* Scroll if content is too tall */
  background: #ffffff;
  min-height: 0; /* Important for flexbox scrolling */
}

/* Agency Grid */
.agency-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.agency-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.agency-card:hover {
  border-color: #a30e0e;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(163, 14, 14, 0.15);
}

.agency-card.selected {
  border-color: #a30e0e;
  background: linear-gradient(135deg, #fff5f5 0%, #ffeaea 100%);
  box-shadow: 0 8px 25px rgba(163, 14, 14, 0.2);
}

.agency-card.dispatched {
  border-color: #28a745;
  background: linear-gradient(135deg, #f0fff4 0%, #e6ffe6 100%);
}

.agency-card.dispatched::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #28a745, #20c997);
}

.agency-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  position: relative;
  flex-shrink: 0;
}

.agency-icon.medical {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.agency-icon.fire {
  background: linear-gradient(135deg, #fd7e14, #e55a00);
}

.agency-icon.police {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.agency-icon.rescue {
  background: linear-gradient(135deg, #20c997, #17a2b8);
}

.agency-icon.hazmat {
  background: linear-gradient(135deg, #ffc107, #e0a800);
}

.agency-icon.k9 {
  background: linear-gradient(135deg, #6f42c1, #5a2d9c);
}

.dispatched-indicator {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #28a745;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: white;
  border: 2px solid white;
}

.agency-info {
  flex: 1;
  min-width: 0; /* Prevent text overflow */
}

.agency-name {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3748;
}

.agency-description {
  margin: 0 0 0.75rem 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.4;
}

.agency-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}
.status-badge.available {
  background: #e3f2fd;
  color: #1976d2;
}

.status-badge.dispatched {
  background: #e8f5e8;
  color: #2e7d32;
}

.status-badge.enroute {
  background: #fff3e0;
  color: #f57c00;
}

.status-badge.busy {
  background: #ffebee;
  color: #c62828;
}

.status-badge.unavailable {
  background: #f5f5f5;
  color: #757575;
}
.response-time {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.selection-indicator {
  opacity: 0;
  color: #a30e0e;
  font-size: 1.25rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.agency-card.selected .selection-indicator {
  opacity: 1;
  transform: scale(1.1);
}

/* Quick Select */
.quick-select {
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
}

.quick-select.disabled {
  opacity: 0.6;
}

.quick-select.disabled {
  pointer-events: none;
  cursor: not-allowed;
}

.quick-select-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.quick-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.quick-btn {
  padding: 0.75rem 1.25rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #495057;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.quick-btn:hover {
  border-color: #a30e0e;
  color: #a30e0e;
  transform: translateY(-1px);
}

.quick-btn.medical {
  border-color: #dc3545;
  color: #dc3545;
}

.quick-btn.fire {
  border-color: #fd7e14;
  color: #fd7e14;
}

.quick-btn.police {
  border-color: #007bff;
  color: #007bff;
}

.quick-btn.all {
  border-color: #6f42c1;
  color: #6f42c1;
}

/* Footer - Fixed at bottom */
.dispatch-footer {
  background: #f8f9fa;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0; /* Prevent footer from shrinking */
  margin-top: auto; /* Push to bottom */
}

.dispatch-footer.disabled {
  opacity: 0.6;
}

.dispatch-footer.disabled * {
  pointer-events: none;
  cursor: not-allowed;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
}

.selected-summary {
  flex: 1;
  min-width: 0;
}

.summary-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #28a745;
  font-weight: 500;
  flex-wrap: wrap;
}

.summary-content i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.selected-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.selected-tag {
  background: #a30e0e;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.no-selection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-style: italic;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-shrink: 0; /* Prevent buttons from shrinking */
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  white-space: nowrap;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  color: white;
}

.btn-primary:hover:not(.disabled) {
  background: linear-gradient(135deg, #8a0b0b 0%, #700000 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(163, 14, 14, 0.3);
}

.btn-primary.disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.selected-count {
  background: rgba(255, 255, 255, 0.3);
  padding: 0.1rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  margin-left: 0.25rem;
}

/* Animations */
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .dispatch-modal-container {
    width: 95%;
    max-width: none;
    margin: 1rem;
    max-height: 95vh; /* More height on mobile */
  }

  .agency-grid {
    grid-template-columns: 1fr;
  }

  .footer-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .selected-tags {
    justify-content: center;
  }

  .action-buttons {
    width: 100%;
    justify-content: stretch;
  }

  .action-buttons .btn {
    flex: 1;
    justify-content: center;
  }

  .quick-buttons {
    justify-content: center;
  }

  /* MODIFIED: Mobile responsive for responders */
  .responder-main-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .responder-name {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }

  .responder-actions {
    justify-content: stretch;
  }

  .btn-force-release {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .dispatch-header {
    padding: 1rem 1.5rem;
  }

  .dispatch-body {
    padding: 1.5rem;
  }

  .dispatch-footer {
    padding: 1rem 1.5rem;
  }

  .header-content {
    flex-direction: column;
    gap: 1rem;
  }

  .title-section {
    flex-direction: column;
    text-align: center;
  }

  .agency-card {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }

  .agency-status {
    justify-content: center;
  }

  .btn {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
}

/* Custom scrollbar for better appearance */
.dispatch-body::-webkit-scrollbar {
  width: 6px;
}

.dispatch-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dispatch-body::-webkit-scrollbar-thumb {
  background: #a30e0e;
  border-radius: 3px;
}

.dispatch-body::-webkit-scrollbar-thumb:hover {
  background: #8a0b0b;
}
</style>
